/**
 * techdays.ts (PLANNER-02 A) — the week of tech-days the planner fills.
 *
 * `pegasus_tech_day_load(from, to)` is one row per tech per day: what's
 * already on the Board (ESC + Pegasus), the cities, off days, the anchor of
 * the existing calls, how many members picked that date online, and the day
 * plan sitting on that tech-day. Room = season cap − reserve − calls.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface TechDayCell {
  empno: string;
  tech_name: string;
  day: string; // YYYY-MM-DD
  calls: number;
  maint: number;
  cities: string | null;
  off: boolean;
  off_label: string | null;
  anchor_lat: number | null;
  anchor_lng: number | null;
  picked: number;
  plan_id: string | null;
  plan_status: string | null;
  plan_origin: string | null;
  plan_stops: number | null;
  plan_booked: number | null;
  /** Home from ESC's technician record (or the tech_home_areas override). */
  home_lat: number | null;
  home_lng: number | null;
  home_label: string | null;
  /** The first "Pick Up Part" ticket of the day: where it is and whether it starts the day. */
  pickup_lat: number | null;
  pickup_lng: number | null;
  pickup_when: "start" | "mid" | null;
  pickup_window: string | null;
  pickup_notes: string | null;
}

/** The Sedgwick office / warehouse — where a part pickup happens unless the ticket says otherwise. */
export interface OfficeLocation { lat: number; lng: number; label: string; address?: string }

export interface TechDayCall {
  id: string;
  source: "esc" | "pegasus";
  custno: string;
  locno: string | null;
  name: string | null;
  address: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  window_code: string | null;
  notes: string | null;
  status: string | null;
  pseudo: boolean;
}

export interface PlannerRules {
  cap: number;          // checks per tech-day in season
  reserve: number;      // slots kept open for no-heat calls
  maxMiles: number;     // how far from the anchor Fill will reach
  homeAreas: Record<string, { lat: number; lng: number; label: string }>;
  office: OfficeLocation | null;
}
export const DEFAULT_RULES: PlannerRules = { cap: 8, reserve: 1, maxMiles: 25, homeAreas: {}, office: null };

const PSEUDO_EMPNOS = new Set(["0000", "0001", "7777", "8888", "9999"]);

export function useTechDayLoad(from: string, to: string) {
  return useQuery({
    queryKey: ["tech_day_load", from, to],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TechDayCell[]> => {
      const { data, error } = await supabase.rpc("pegasus_tech_day_load", { p_from: from, p_to: to });
      if (error) throw error;
      return ((data ?? []) as TechDayCell[]).filter((c) => !PSEUDO_EMPNOS.has(c.empno));
    },
  });
}

export function useTechDayCalls(empno: string | null, day: string | null) {
  return useQuery({
    queryKey: ["tech_day_calls", empno, day],
    enabled: Boolean(empno && day),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TechDayCall[]> => {
      const { data, error } = await supabase.rpc("pegasus_tech_day_calls", { p_empno: empno, p_date: day });
      if (error) throw error;
      return (data ?? []) as TechDayCall[];
    },
  });
}

export function usePlannerRules() {
  return useQuery({
    queryKey: ["planner_rules"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PlannerRules> => {
      const { data, error } = await supabase
        .from("pegasus_plan_config")
        .select("key, value")
        .in("key", ["season_tech_day_cap", "season_reserve_slots", "planner_max_miles", "tech_home_areas", "office_location"]);
      if (error) throw error;
      const m = new Map(((data ?? []) as { key: string; value: unknown }[]).map((r) => [r.key, r.value]));
      const num = (k: string, d: number) => { const v = m.get(k); return typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)) ? Number(v) : d; };
      const areas = (m.get("tech_home_areas") ?? {}) as PlannerRules["homeAreas"];
      const off = m.get("office_location") as OfficeLocation | undefined;
      return { cap: num("season_tech_day_cap", 8), reserve: num("season_reserve_slots", 1), maxMiles: num("planner_max_miles", 25), homeAreas: areas && typeof areas === "object" ? areas : {}, office: off && typeof off.lat === "number" && typeof off.lng === "number" ? off : null };
    },
  });
}

export function useSetPlannerRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { key: "season_tech_day_cap" | "season_reserve_slots" | "planner_max_miles" | "tech_home_areas"; value: unknown }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_plan_config")
        .upsert({ key: v.key, value: v.value, updated_by: u?.user?.email ?? null, updated_at: new Date().toISOString() }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planner_rules"] });
      qc.invalidateQueries({ queryKey: ["tech_day_load"] });
    },
  });
}

/** Room on a tech-day: the season cap, minus the slots kept open, minus what's already booked. */
export function roomFor(cell: TechDayCell, rules: PlannerRules): number {
  if (cell.off) return 0;
  return Math.max(0, rules.cap - rules.reserve - cell.calls);
}

export const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** The Monday of the week that holds `d` (today → this week; Sat/Sun → next). */
export function weekMonday(d = new Date()): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay(); // 0 Sun … 6 Sat
  if (dow === 0) x.setDate(x.getDate() + 1);
  else if (dow === 6) x.setDate(x.getDate() + 2);
  else x.setDate(x.getDate() - (dow - 1));
  return x;
}

/** Mon–Fri of the week starting `monday`. */
export function weekDays(monday: Date): string[] {
  return [0, 1, 2, 3, 4].map((i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return isoDate(d); });
}

export const fmtDay = (iso: string, opts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" }) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", opts);

export const firstName = (full: string | null | undefined) => (full ?? "").trim().split(/\s+/)[0] || "";
